import type { CurrencyCode } from '../../types/executiveAnalytics';

const CURRENCY_CONFIG: Record<CurrencyCode, { symbol: string; locale: string }> = {
  USD: { symbol: '$', locale: 'en-US' },
  GBP: { symbol: '\u00A3', locale: 'en-GB' },
  EUR: { symbol: '\u20AC', locale: 'de-DE' },
  AUD: { symbol: 'A$', locale: 'en-AU' },
  CAD: { symbol: 'C$', locale: 'en-CA' },
  CHF: { symbol: 'CHF', locale: 'de-CH' },
  JPY: { symbol: '\u00A5', locale: 'ja-JP' },
  INR: { symbol: '\u20B9', locale: 'en-IN' },
  SGD: { symbol: 'S$', locale: 'en-SG' },
  HKD: { symbol: 'HK$', locale: 'en-HK' },
  NZD: { symbol: 'NZ$', locale: 'en-NZ' },
  SEK: { symbol: 'kr', locale: 'sv-SE' },
  NOK: { symbol: 'kr', locale: 'nb-NO' },
  DKK: { symbol: 'kr', locale: 'da-DK' },
  ZAR: { symbol: 'R', locale: 'en-ZA' },
  AED: { symbol: 'AED', locale: 'ar-AE' },
  BRL: { symbol: 'R$', locale: 'pt-BR' },
  MXN: { symbol: 'MX$', locale: 'es-MX' },
};

export function formatCurrencyValue(
  value: number | null | undefined,
  currency: CurrencyCode = 'USD',
): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000_000) {
    return `${sign}${config.symbol}${(abs / 1_000_000_000).toFixed(1)}B`;
  }
  if (abs >= 1_000_000) {
    return `${sign}${config.symbol}${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${config.symbol}${(abs / 1_000).toFixed(0)}K`;
  }
  return `${sign}${config.symbol}${abs.toLocaleString(config.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function formatCurrencyPrecise(
  value: number | null | undefined,
  currency: CurrencyCode = 'USD',
): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000) {
    return `${sign}${config.symbol}${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${config.symbol}${(abs / 1_000).toFixed(1)}K`;
  }
  return `${sign}${config.symbol}${abs.toLocaleString(config.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function getCurrencySymbol(currency: CurrencyCode = 'USD'): string {
  return (CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD).symbol;
}

const HQ_COUNTRY_CURRENCY: Record<string, CurrencyCode> = {
  'united kingdom': 'GBP',
  'uk': 'GBP',
  'great britain': 'GBP',
  'england': 'GBP',
  'scotland': 'GBP',
  'wales': 'GBP',
  'germany': 'EUR',
  'france': 'EUR',
  'italy': 'EUR',
  'spain': 'EUR',
  'netherlands': 'EUR',
  'belgium': 'EUR',
  'ireland': 'EUR',
  'austria': 'EUR',
  'finland': 'EUR',
  'portugal': 'EUR',
  'greece': 'EUR',
  'australia': 'AUD',
  'canada': 'CAD',
  'switzerland': 'CHF',
  'japan': 'JPY',
  'india': 'INR',
  'singapore': 'SGD',
  'hong kong': 'HKD',
  'new zealand': 'NZD',
  'sweden': 'SEK',
  'norway': 'NOK',
  'denmark': 'DKK',
  'south africa': 'ZAR',
  'uae': 'AED',
  'united arab emirates': 'AED',
  'brazil': 'BRL',
  'mexico': 'MXN',
};

export function inferCurrencyFromHq(hqLocation: string | null | undefined): CurrencyCode {
  if (!hqLocation) return 'USD';
  const normalized = hqLocation.toLowerCase().trim();
  for (const [key, code] of Object.entries(HQ_COUNTRY_CURRENCY)) {
    if (normalized.includes(key)) return code;
  }
  return 'USD';
}
